import React from 'react';
import { CircleCheckIcon, CircleSlashIcon } from 'lucide-react';
import moment from 'moment';
import { formatDateV2 } from 'utils/formatters/Date';
import Link from 'components/v2/Link.js';
import StatusBadge from './StatusBadge';
import { CustomerTypeBadge } from './CustomerTypeBadge';
import _ from 'lodash';
import { sluggify } from 'utils/localization/utils';
import { useTranslation } from 'react-i18next';

export const createCellFormatter =
  (formatter) =>
  ({
    column: {
      columnDef: { accessorKey },
    },
    row,
  }) =>
    formatter(row.getValue(accessorKey));

export const formatLocationCell = (value) => (
  <div>{(value || '').replace(/,(?!\s)/g, ', ')}</div>
);

export const formatStatusCell = ({ row }) => {
  const value = row.getValue('status');

  return <StatusBadge status={value} />;
};

export const formatOriginCell = ({ row }) => {
  return <div>{row.getValue('origin').replace(/,(?!\s)/g, ', ')}</div>;
};

export const formatDestinationCell = ({ row }) => {
  return <div>{row.getValue('destination').replace(/,(?!\s)/g, ', ')}</div>;
};

export function formatBooleanValue(value) {
  if (value) {
    return (
      <div className="flex justify-end text-right">
        <CircleCheckIcon className="w-4 h-4 text-green-500 text-right" />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-end text-right">
        <CircleSlashIcon className="w-4 h-4 text-red-500 text-right" />
      </div>
    );
  }
  return null;
}

export function formatTextValue(value) {
  return value || '-';
}

export function formatNumericValue(value) {
  return <div className="text-right">{value || '-'}</div>;
}

export function formatDollarValue(value) {
  if (!value) return formatNumericValue();

  const amount = parseFloat(value).toFixed(2);
  const formatted = new Intl.NumberFormat('en-us', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

  return formatNumericValue(formatted);
}

export const formatSequenceCell = ({ row }) => {
  const sequence = row.original.sequenceCount;

  const sequenceCount = row.original.totalCount;

  if (!sequence || !sequenceCount) {
    return '-';
  }

  return `${sequence} of ${sequenceCount} `;
};

export const formatCustomerTypeCell = ({ row }) => {
  const value = row.getValue('customerType');
  return <CustomerTypeBadge customerType={value} />;
};

export function formatCustomerCell({ row }) {
  const customerId = row?.original?.customerId;
  if (!customerId) return <div>{formatTextValue(null)}</div>;
  return (
    <Link url={`/users/${customerId}`}>
      {formatTextValue(row.getValue('customerName'))}
    </Link>
  );
}

export const formatRateCell = ({ row }) => {
  const rateValue = row.getValue('rate');
  if (!rateValue) {
    return formatNumericValue('-');
  }
  const amount = parseFloat(row.getValue('rate')).toFixed(2);
  const formatted = new Intl.NumberFormat('en-us', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

  return formatNumericValue(formatted);
};

export function formatDateCell(date) {
  const value = date ? moment.utc(date).format('MM/DD/YYYY') : '-';
  return value;
}

export const formatUpdatedAtCell = ({ row }) => {
  const rowValue = row.getValue('laneRatesUpdatedAt');
  return rowValue ? moment(rowValue).fromNow(true) : '-';
};

export const formatCreatedAtCell = ({ row }) => {
  const rowValue = row.getValue('laneRatesCreatedAt');
  return formatDateCell(rowValue);
};

export const formatValidityStart = ({ row }) => {
  const rowValue = row.getValue('effectiveStart');
  return formatDateCell(rowValue);
};

export const formatValidityEnd = ({ row }) => {
  const rowValue = row.getValue('effectiveEnd');
  return formatDateCell(rowValue);
};

export const formatReversibleCell = ({ row }) => {
  const reversible = row.getValue('reversible');

  return formatBooleanValue(reversible);
};

export const formatBillRoundtripCell = ({ row }) => {
  const rowValue = row.getValue('billRoundtrip');

  return formatBooleanValue(rowValue);
};

export const formatCommittmentCell = ({ row }) => {
  const value = row.getValue('commitment');

  return formatNumericValue(value);
};

export function getRateConditions(row) {
  return row.original?.rateConditions || {};
}

export function formatHazardous(criteria) {
  return criteria;
}

export function getRateConditionCriteria(key, row) {
  const { t } = useTranslation();
  const criteria = getRateConditions(row)?.[key]?.criteria;
  if (['hazmat', 'hazardous'].includes(key)) return formatHazardous(criteria);
  return t(sluggify(criteria));
}

export const formatCustomerRateConditionCell = ({ row }) => {
  const customerCondition = getRateConditions(row).customer,
    customerId = customerCondition?.criteria,
    displayName = customerCondition?.meta?.customer?.name;
  if (!customerId) return <div>{formatTextValue(null)}</div>;
  return (
    <Link url={`/users/${customerId}`}>
      {formatTextValue(displayName ?? customerId)}
    </Link>
  );
};

export const formatEquipmenSizeRateConditionCell = ({ row }) => (
  <div className="text-right">
    {formatTextValue(getRateConditionCriteria('equipmentSize', row))}
  </div>
);

export const formatEquipmenTypeRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('equipmentType', row));

export const formatHazardousRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('hazardous', row));

export const formatMarketRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('market', row));

export const formatOrderTypeRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('orderType', row));

export const formatSteamshipLineRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('steamshipOrRailroad', row));
